import { useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { useRelayEnvironment } from 'react-relay';
import { ConnectionHandler, commitLocalUpdate, getRelayHandleKey } from 'relay-runtime';
import type { firstGroupIssues_childIssuesQuery$variables } from '@atlassian/jira-relay/src/__generated__/firstGroupIssues_childIssuesQuery.graphql';
import { useHierarchyActions } from '../../../../controllers/hierarchy/index.tsx';

const JIRA = 'jira';
const HIERARCHY_ISSUE_SEARCH_CONNECTION = 'hierarchy_nativeIssueTable__issueSearch';
const JIRA_ISSUE_CONNECTION_TYPE = 'JiraIssueConnection';
const CONNECTION = 'connection';

type FieldSetsInput = firstGroupIssues_childIssuesQuery$variables['fieldSetsInput'];

export type Props = {
	groupId: string;
	groupConnectionId: string;
	firstGroupItemsConnectionId: string;
	issueCount?: number | null;
	issueSearchVariables: Pick<
		firstGroupIssues_childIssuesQuery$variables,
		'cloudId' | 'issueSearchInput' | 'fieldSetsInput'
	>;
};

export const useInitialiseFirstGroupIssues = ({
	groupId,
	groupConnectionId,
	firstGroupItemsConnectionId,
	issueSearchVariables,
	issueCount,
}: Props) => {
	const environment = useRelayEnvironment();
	const hasInitialised = useRef(false);
	const prevGroupConnectionId = useRef<string>(groupConnectionId);
	const prevFieldSetsInput = useRef<FieldSetsInput>(issueSearchVariables.fieldSetsInput);

	const { setIsItemExpanded } = useHierarchyActions();

	// If the groupConnectionId or fieldSetsInput changes, trigger re-initialisation
	if (
		prevGroupConnectionId.current !== groupConnectionId ||
		!isEqual(prevFieldSetsInput.current, issueSearchVariables.fieldSetsInput)
	) {
		hasInitialised.current = false;
		prevGroupConnectionId.current = groupConnectionId;
		prevFieldSetsInput.current = issueSearchVariables.fieldSetsInput;
	}

	if (!hasInitialised.current) {
		setIsItemExpanded(
			{
				itemId: groupId,
				groupId,
				itemsConnectionId: groupConnectionId,
			},
			true,
		);

		commitLocalUpdate(environment, (store) => {
			const firstGroupIssuesConnection = store.get(firstGroupItemsConnectionId);
			const jiraRecord = store.getRoot().getLinkedRecord(JIRA);
			if (!firstGroupIssuesConnection || !jiraRecord) {
				return;
			}

			const connectionId = ConnectionHandler.getConnectionID(
				jiraRecord.getDataID(),
				HIERARCHY_ISSUE_SEARCH_CONNECTION,
				issueSearchVariables,
			);
			if (!store.get(connectionId)) {
				const connection = store.create(connectionId, JIRA_ISSUE_CONNECTION_TYPE);
				connection.copyFieldsFrom(firstGroupIssuesConnection);
				jiraRecord.setLinkedRecord(
					connection,
					getRelayHandleKey(CONNECTION, HIERARCHY_ISSUE_SEARCH_CONNECTION),
					issueSearchVariables,
				);

				// JSC-1021 - first group issues connection is currently returning results capped at 100 for the totalIssueSearchCount
				// This is a workaround while we determine if there is any performance impact to changing this on the BE.
				if (typeof issueCount === 'number') {
					connection.setValue(issueCount, 'totalIssueSearchResultCount');
				}
			}

			hasInitialised.current = true;
		});
	}

	return hasInitialised.current;
};
